/**
 * Écran de conversion : déposer un catalogue, vérifier les colonnes, générer.
 *
 * Les règles ne se règlent pas ici — elles vivent dans leur propre écran et
 * sont enregistrées en base. Cet écran se contente de rappeler celles qui vont
 * s'appliquer, pour qu'on sache ce qu'on produit avant de le produire.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { ApiError, conversion } from '@/api/client'
import type {
  CatalogAnalysis,
  ColumnMapping,
  ConversionResponse,
  MergeSummary,
  PreparationSummary,
  Profile,
  SomColumn,
  SomReferenceSettings,
  SomStatus,
  SomSummary,
} from '@/api/types'
import { Dropzone, FileBar } from '@/components/Dropzone'
import { StatusPill, type StatusTone } from '@/components/Layout'
import {
  Button,
  Card,
  CardBody,
  CardHead,
  CardNote,
  Checkbox,
  Field,
  Grid,
  Message,
  Scroll,
  SectionHead,
  Table,
  Tag,
  numeric,
} from '@/components/ui'
import {
  useMeta,
  useProfile,
  useProfiles,
  useRemoveSomReference,
  useSomReference,
  useUpdateSomReference,
  useUploadSomReference,
} from '@/hooks/useApi'
import { formatBytes, formatDateTime, toBlobUrl, toFile, triggerDownload } from '@/lib/files'
import { FILE_LABELS } from '@/lib/rules'
import styles from './ConvertPage.module.css'

const PREVIEW_COLUMNS: { key: keyof ConversionResponse['preview'][number]; label: string }[] = [
  { key: 'ean', label: 'EAN' },
  { key: 'marque', label: 'Marque' },
  { key: 'modele', label: 'Modèle' },
  { key: 'achat', label: "Prix d'achat" },
  { key: 'fournisseur', label: 'Fournisseur' },
  { key: 'poids', label: 'Poids (kg)' },
  { key: 'stock', label: 'Stock' },
  { key: 'qte', label: 'Qté publiée' },
  { key: 'delai', label: 'Délai (j)' },
  { key: 'commission', label: 'Commission' },
  { key: 'marge', label: 'Marge' },
  { key: 'amazon', label: 'Amazon' },
  { key: 'darty', label: 'Darty' },
  { key: 'youkapi', label: 'Youkapi' },
]

export function ConvertPage() {
  const { data: meta } = useMeta()
  const { data: profileList } = useProfiles()

  const [file, setFile] = useState<File | null>(null)
  //: Le catalogue SOM, lui, vit sur le serveur : on ne choisit ici que de
  //: comparer ou non l'export VDB du jour avec lui.
  const [somCompare, setSomCompare] = useState(true)
  const [sheet, setSheet] = useState('')
  const [headerRow, setHeaderRow] = useState(1)
  const [analysis, setAnalysis] = useState<CatalogAnalysis | null>(null)
  const [mapping, setMapping] = useState<ColumnMapping>({})
  const [profileId, setProfileId] = useState<string>('')
  const [result, setResult] = useState<ConversionResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<{ tone: StatusTone; text: string }>({ tone: 'idle', text: 'Prêt' })
  const [analyzing, setAnalyzing] = useState(false)
  const [converting, setConverting] = useState(false)
  const [fetching, setFetching] = useState(false)

  const { data: profile } = useProfile(profileId || undefined)
  const { data: somRef } = useSomReference()
  const uploadSom = useUploadSomReference()
  const updateSom = useUpdateSomReference()
  const removeSom = useRemoveSomReference()
  const [somError, setSomError] = useState<string | null>(null)

  // Le profil par défaut est présélectionné, mais reste modifiable : on peut
  // vouloir produire un fichier avec un barème promotionnel sans changer
  // le réglage courant de l'équipe.
  useEffect(() => {
    if (!profileId && profileList?.length) {
      setProfileId((profileList.find((item) => item.is_default) ?? profileList[0]!).id)
    }
  }, [profileList, profileId])

  // Les URL d'objets créées pour les téléchargements sont révoquées dès qu'un
  // nouveau résultat arrive : sans cela chaque conversion laisserait plusieurs
  // mégaoctets attachés à l'onglet.
  const blobUrls = useRef<string[]>([])
  useEffect(() => {
    return () => {
      blobUrls.current.forEach((url) => URL.revokeObjectURL(url))
      blobUrls.current = []
    }
  }, [])

  function keepUrl(url: string): string {
    blobUrls.current.push(url)
    return url
  }

  function resetResult() {
    blobUrls.current.forEach((url) => URL.revokeObjectURL(url))
    blobUrls.current = []
    setResult(null)
  }

  /**
   * Relit le catalogue côté serveur, et rend l'étape 2 conforme à ce que la
   * conversion produira.
   *
   * Tout ce qui vient de changer voyage explicitement plutôt que d'être lu dans
   * l'état : `setState` n'a pas encore rendu la main quand l'appel part. Ce
   * n'est pas qu'une précaution de React — le profil porte les règles de
   * préparation : le lire en retard montrerait un tri que la conversion ne
   * referait pas à l'identique.
   */
  async function analyze(
    next: File,
    overrides: { sheet?: string; headerRow?: number; profileId?: string; somCompare?: boolean } = {},
  ) {
    setAnalyzing(true)
    setError(null)
    setStatus({ tone: 'busy', text: 'Lecture du fichier…' })
    try {
      const data = await conversion.analyze({
        file: next,
        sheet: overrides.sheet ?? '',
        headerRow: overrides.headerRow ?? headerRow,
        profileId: overrides.profileId ?? profileId,
        somCompare: overrides.somCompare ?? somCompare,
      })
      setAnalysis(data)
      setMapping(data.mapping)
      setSheet(data.sheet ?? '')
      setStatus({
        tone: data.som_status === 'missing_columns' ? 'busy' : 'ok',
        text:
          data.som_status === 'merged'
            ? 'Catalogues fusionnés'
            : data.som_status === 'missing_columns'
              ? 'Colonnes SOM à confirmer'
              : data.preparation
                ? 'Catalogue préparé'
                : 'Catalogue chargé',
      })
    } catch (cause) {
      setAnalysis(null)
      setError(cause instanceof ApiError ? cause.message : 'Lecture impossible.')
      setStatus({ tone: 'error', text: 'Erreur de lecture' })
    } finally {
      setAnalyzing(false)
    }
  }

  /**
   * Toute modification du catalogue SOM — dépôt, correction, retrait — refait
   * aussitôt la comparaison du fichier chargé : l'étape 2 doit montrer ce que
   * la conversion produira.
   */
  async function changeSom(action: () => Promise<unknown>) {
    setSomError(null)
    resetResult()
    try {
      await action()
    } catch (cause) {
      setSomError(cause instanceof ApiError ? cause.message : 'Opération impossible.')
      return
    }
    if (file) await analyze(file, { sheet })
  }

  function uploadSomFile(next: File) {
    void changeSom(() => uploadSom.mutateAsync(next))
  }

  function updateSomSettings(settings: SomReferenceSettings) {
    void changeSom(() => updateSom.mutateAsync(settings))
  }

  function removeSomFile() {
    if (!window.confirm('Retirer le catalogue SOM ? Les exports VDB ne seront plus comparés tant qu’un autre n’est pas chargé.')) return
    void changeSom(() => removeSom.mutateAsync())
  }

  function toggleSomCompare(next: boolean) {
    setSomCompare(next)
    resetResult()
    if (file) void analyze(file, { sheet, somCompare: next })
  }

  function chooseFile(next: File) {
    setFile(next)
    resetResult()
    void analyze(next)
  }

  /** Télécharge l'export du jour chez le fournisseur, puis l'analyse. */
  async function fetchFromSupplier() {
    setFetching(true)
    setError(null)
    setStatus({ tone: 'busy', text: 'Téléchargement du catalogue…' })
    try {
      const next = toFile(await conversion.fetchSupplier())
      setFile(next)
      resetResult()
      await analyze(next)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Téléchargement impossible.')
      setStatus({ tone: 'error', text: 'Téléchargement impossible' })
    } finally {
      setFetching(false)
    }
  }

  const missingRequired = useMemo(() => {
    if (!meta) return []
    return meta.fields.filter((field) => field.required && mapping[field.key] == null)
  }, [meta, mapping])

  async function run() {
    if (!file) return
    setConverting(true)
    setError(null)
    resetResult()
    setStatus({ tone: 'busy', text: 'Conversion en cours…' })
    try {
      const data = await conversion.run({
        file, sheet, headerRow, mapping, profileId, somCompare,
      })
      setResult(data)
      setStatus({ tone: 'ok', text: 'Fichiers prêts' })
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Conversion impossible.')
      setStatus({ tone: 'error', text: 'Échec de la conversion' })
    } finally {
      setConverting(false)
    }
  }

  if (!meta) return null

  return (
    <>
      <StatusPill tone={status.tone}>{status.text}</StatusPill>

      {/* --- 1. Catalogue ------------------------------------------------ */}
      <section className={styles.step}>
        <SectionHead title="1. Catalogue fournisseur">
          Récupérez l’export du jour, ou déposez un fichier. Formats acceptés : l’export
          brut du fournisseur (.xls), .xlsx, .xlsm et .csv.
        </SectionHead>

        {!file ? (
          <>
            <Dropzone onFile={chooseFile} maxUploadMb={meta.max_upload_mb} />
            {meta.supplier_url ? (
              <div className={styles.fetchRow}>
                <Button variant="secondary" busy={fetching} onClick={() => void fetchFromSupplier()}>
                  {fetching ? 'Téléchargement…' : 'Récupérer chez le fournisseur'}
                </Button>
                <span className={styles.runHint}>
                  Télécharge l’export du jour depuis {hostOf(meta.supplier_url)} et le prépare aussitôt.
                </span>
              </div>
            ) : null}
          </>
        ) : (
          <Card>
            <CardHead
              title=""
              actions={
                <Button
                  variant="secondary"
                  small
                  onClick={() => {
                    setFile(null)
                    setAnalysis(null)
                    setSomCompare(true)
                    resetResult()
                    setStatus({ tone: 'idle', text: 'Prêt' })
                  }}
                >
                  Changer de fichier
                </Button>
              }
            />
            <CardBody>
              <FileBar name={file.name} size={file.size} />
              {analysis ? (
                <Grid columns={2} className={styles.step}>
                  <Field label="Onglet" htmlFor="sheet">
                    <select
                      id="sheet"
                      value={sheet}
                      disabled={analysis.sheets.length < 2}
                      onChange={(event) => {
                        setSheet(event.target.value)
                        void analyze(file, { sheet: event.target.value, headerRow })
                      }}
                    >
                      {(analysis.sheets.length ? analysis.sheets : [analysis.sheet ?? '']).map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Ligne des en-têtes" htmlFor="headerRow">
                    <input
                      id="headerRow"
                      type="number"
                      min={1}
                      value={headerRow}
                      onChange={(event) => setHeaderRow(Number(event.target.value) || 1)}
                      onBlur={() => file && void analyze(file, { sheet, headerRow })}
                    />
                  </Field>
                </Grid>
              ) : null}
            </CardBody>
          </Card>
        )}

        {analysis?.preparation ? (
          <div className={styles.reveal}>
            <PreparationCard preparation={analysis.preparation} keepUrl={keepUrl} />
          </div>
        ) : null}
      </section>

      {/* --- 1 bis. Catalogue SOM de référence --------------------------- */}
      <section className={styles.step}>
        <SectionHead title="1 bis. Catalogue SOM de référence">
          Chargé deux fois par an (NOVY, BOSCH/SIEMENS, SMEG…), il reste sur le serveur. Chaque
          export VDB y est comparé : le meilleur prix d’achat l’emporte, sauf pour un produit en
          stock chez VDB, qui part au prix VDB. Un fichier d’un autre fournisseur (CEBEO…) se
          convertit seul.
        </SectionHead>

        <SomReferenceCard
          reference={somRef ?? null}
          busy={uploadSom.isPending || updateSom.isPending || removeSom.isPending}
          onUpload={uploadSomFile}
          onRemove={removeSomFile}
          onSettings={updateSomSettings}
        />
        {somError ? <Message kind="error">{somError}</Message> : null}

        {analysis ? (
          <div className={styles.reveal}>
            <SomOutcome
              status={analysis.som_status}
              som={analysis.som}
              compare={somCompare}
              onCompare={toggleSomCompare}
              keepUrl={keepUrl}
            />
          </div>
        ) : null}
      </section>

      {/* --- 2. Colonnes -------------------------------------------------- */}
      {analysis ? (
        <section className={`${styles.step} ${styles.reveal}`}>
          <SectionHead title="2. Correspondance des colonnes">
            {analysis.preparation
              ? 'Les colonnes sont celles du catalogue préparé, donc déjà reconnues.'
              : 'Les colonnes sont reconnues automatiquement. Vérifiez et corrigez si nécessaire.'}
          </SectionHead>

          <Card>
            <CardHead
              title="Champs du convertisseur"
              hint={
                <>
                  {analysis.row_count} ligne(s) détectée(s)
                  <br />
                  {analysis.headers.length} colonne(s)
                </>
              }
            />
            <CardBody tight>
              {meta.fields.map((field) => {
                const missing = field.required && mapping[field.key] == null
                return (
                  <div key={field.key} className={`${styles.maprow} ${missing ? styles.missing : ''}`}>
                    <div className={styles.field}>
                      {field.label}
                      {field.required ? <em className={styles.required}>REQUIS</em> : null}
                    </div>
                    <div className={styles.arrow} aria-hidden>
                      →
                    </div>
                    <div>
                      <select
                        aria-label={`Colonne pour ${field.label}`}
                        value={mapping[field.key] ?? ''}
                        onChange={(event) =>
                          setMapping((current) => ({
                            ...current,
                            [field.key]: event.target.value === '' ? null : Number(event.target.value),
                          }))
                        }
                      >
                        <option value="">— non utilisé —</option>
                        {analysis.headers.map((header, index) => (
                          <option key={`${header}-${index}`} value={index}>
                            {header || `colonne ${index + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )
              })}
            </CardBody>
          </Card>

          <Card>
            <CardHead title="Aperçu du fichier" hint="8 premières lignes" />
            <CardBody>
              <Scroll>
                <Table>
                  <thead>
                    <tr>
                      {analysis.headers.map((header, index) => (
                        <th key={`${header}-${index}`}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.preview.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {analysis.headers.map((_, index) => (
                          <td key={index}>{row[index] ?? ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Scroll>
            </CardBody>
          </Card>
        </section>
      ) : null}

      {/* --- 3. Génération ------------------------------------------------ */}
      {analysis ? (
        <section className={`${styles.step} ${styles.reveal}`}>
          <SectionHead title="3. Génération des fichiers">
            Les prix sont calculés avec le profil de règles sélectionné.
          </SectionHead>

          <Card>
            <CardHead title="Règles appliquées" />
            <CardBody>
              <div className={styles.profileRow}>
                <Field label="Profil de règles" htmlFor="profile">
                  <select
                    id="profile"
                    value={profileId}
                    onChange={(event) => {
                      const next = event.target.value
                      setProfileId(next)
                      // Le profil porte aussi les règles de préparation : le
                      // catalogue est relu, sinon l'étape 2 montrerait un tri
                      // que la conversion ne referait pas à l'identique.
                      if (file) void analyze(file, { sheet, headerRow, profileId: next })
                    }}
                  >
                    {(profileList ?? []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                        {item.is_default ? ' (par défaut)' : ''} — v{item.version}
                      </option>
                    ))}
                  </select>
                </Field>
                <Link to="/regles" style={{ fontSize: 13.5, fontWeight: 600, paddingBottom: 8 }}>
                  Modifier les règles →
                </Link>
              </div>

              {profile ? <RulesRecap profile={profile} amazonTemplate={meta.amazon_template.custom_name} /> : null}
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <div className={styles.runRow}>
                <Button onClick={() => void run()} busy={converting} disabled={missingRequired.length > 0}>
                  {converting ? 'Conversion…' : 'Générer les fichiers'}
                </Button>
                <span className={styles.runHint}>
                  {missingRequired.length
                    ? `Colonnes obligatoires non associées : ${missingRequired.map((f) => f.label).join(', ')}`
                    : 'Calcul des prix puis écriture des templates.'}
                </span>
              </div>
            </CardBody>
          </Card>

          {error ? <Message kind="error">{error}</Message> : null}

          {result ? <Results result={result} keepUrl={keepUrl} /> : null}
        </section>
      ) : null}

      {error && !analysis ? <Message kind="error">{error}</Message> : null}
      {analyzing && !analysis ? <Message kind="info">Lecture du fichier…</Message> : null}
    </>
  )
}

/**
 * Un bouton de choix de fichier, sans zone de dépôt.
 *
 * Le catalogue SOM se dépose à côté du catalogue du jour : une seconde grande
 * zone de glisser-déposer volerait la vedette au fichier principal, et on ne
 * saurait plus lequel des deux on remplace.
 */
function FilePicker({ label, onFile }: { label: string; onFile: (file: File) => void }) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <>
      <Button variant="secondary" small onClick={() => input.current?.click()}>
        {label}
      </Button>
      <input
        ref={input}
        type="file"
        accept=".xlsx,.xlsm,.csv,.txt,.xls"
        hidden
        onChange={(event) => {
          const chosen = event.target.files?.[0]
          if (chosen) onFile(chosen)
          event.target.value = ''
        }}
      />
    </>
  )
}

/** D'où vient la colonne retenue — ce qui mérite un coup d'œil, et ce qui non. */
const SOURCE_LABELS: Record<string, { text: string; tone: 'ok' | 'warn' }> = {
  header: { text: 'nom de colonne', tone: 'ok' },
  content: { text: 'deviné au contenu', tone: 'warn' },
  manual: { text: 'choisi', tone: 'ok' },
}

/**
 * Le catalogue SOM gardé sur le serveur : fichier, colonnes reconnues, aperçu.
 *
 * La table des colonnes est le cœur de la carte. Le fichier change de
 * vocabulaire chaque année ; la détection s'en tire le plus souvent seule, mais
 * quand elle se trompe, c'est ici — et seulement ici — que ça se rattrape. La
 * correction est gardée avec le fichier : elle vaut jusqu'au prochain dépôt.
 * Repliée par défaut, puisqu'elle ne sert que deux fois par an ; ouverte
 * d'office quand une colonne obligatoire manque.
 */
function SomReferenceCard({
  reference,
  busy,
  onUpload,
  onRemove,
  onSettings,
}: {
  reference: SomSummary | null
  busy: boolean
  onUpload: (file: File) => void
  onRemove: () => void
  onSettings: (settings: SomReferenceSettings) => void
}) {
  const [open, setOpen] = useState(false)
  const missing = Boolean(reference?.missing.length)
  const expanded = Boolean(reference) && (open || missing)

  return (
    <>
      <Card>
        <CardHead
          title="Catalogue SOM"
          hint={reference ? `chargé le ${formatDateTime(reference.uploaded_at)}` : 'aucun fichier'}
          actions={
            reference ? (
              <>
                {!missing ? (
                  <Button variant="secondary" small onClick={() => setOpen((value) => !value)}>
                    {expanded ? 'Masquer les colonnes' : 'Vérifier les colonnes'}
                  </Button>
                ) : null}
                <Button variant="secondary" small disabled={busy} onClick={onRemove}>
                  Retirer
                </Button>
              </>
            ) : undefined
          }
        />
        <CardBody>
          {reference ? (
            <>
              <FileBar name={reference.filename} size={reference.size ?? 0} />
              <CardNote>
                {reference.row_count} ligne(s) — comparé automatiquement à chaque export VDB.
              </CardNote>
            </>
          ) : (
            <CardNote>
              Aucun catalogue SOM n’est chargé : les exports VDB se convertissent seuls. L’ordre et
              le nom des colonnes changent d’une année à l’autre : elles sont reconnues
              automatiquement, et restent corrigeables une par une.
            </CardNote>
          )}
          <FilePicker
            label={busy ? 'Envoi…' : reference ? 'Remplacer le fichier SOM' : 'Charger le fichier SOM'}
            onFile={(next) => {
              setOpen(true)
              onUpload(next)
            }}
          />
        </CardBody>
      </Card>

      {reference && missing ? (
        <Message kind="error">
          <b>Colonne(s) obligatoire(s) introuvable(s) dans le fichier SOM : {reference.missing.join(', ')}</b>
          <br />
          Tant qu’elles manquent, rien n’est comparé : les exports VDB partent seuls. Désignez les
          colonnes ci-dessous.
        </Message>
      ) : null}

      {reference && expanded ? (
        <>
          {reference.sheets.length > 1 ? (
            <Card>
              <CardBody>
                <Field label="Onglet du classeur SOM" htmlFor="somSheet">
                  <select
                    id="somSheet"
                    value={reference.sheet ?? ''}
                    disabled={busy}
                    onChange={(event) => onSettings({ sheet: event.target.value })}
                  >
                    {reference.sheets.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </Field>
              </CardBody>
            </Card>
          ) : null}

          <Card>
            <CardHead
              title="Colonnes reconnues dans le fichier SOM"
              hint={
                <>
                  {reference.row_count} ligne(s)
                  <br />
                  {reference.headers.length} colonne(s)
                </>
              }
            />
            <CardBody tight>
              {reference.columns.map((column) => (
                <SomColumnRow
                  key={column.role}
                  column={column}
                  headers={reference.headers}
                  value={column.index}
                  onChoose={(role, index) => onSettings({ columns: { [role]: index } })}
                />
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardHead title="Aperçu du fichier SOM" hint="8 premières lignes" />
            <CardBody>
              <Scroll>
                <Table>
                  <thead>
                    <tr>
                      {reference.headers.map((header, index) => (
                        <th key={`${header}-${index}`}>{header || `colonne ${index + 1}`}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reference.preview.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {reference.headers.map((_, index) => (
                          <td key={index}>{row[index] ?? ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Scroll>
            </CardBody>
          </Card>
        </>
      ) : null}
    </>
  )
}

/** Ce qu'est devenu le fichier déposé face au catalogue SOM. */
const SOM_STATUS_NOTES: Record<Exclude<SomStatus, 'merged' | 'missing_columns'>, string> = {
  no_reference: 'Aucun catalogue SOM n’est chargé : l’export VDB part seul, à ses propres prix.',
  not_vdb:
    'Ce fichier n’est pas un export VDB : il se convertit tel quel, sans comparaison avec le catalogue SOM.',
  disabled: 'Comparaison décochée : l’export VDB part seul, à ses propres prix.',
}

function SomOutcome({
  status,
  som,
  compare,
  onCompare,
  keepUrl,
}: {
  status: SomStatus
  som: SomSummary | null
  compare: boolean
  onCompare: (next: boolean) => void
  keepUrl: (url: string) => string
}) {
  return (
    <>
      {status !== 'not_vdb' ? (
        <Checkbox
          checked={compare}
          onChange={onCompare}
          label="Comparer cet export VDB avec le catalogue SOM"
        />
      ) : null}

      {status === 'merged' && som?.merge ? (
        <MergeCard merge={som.merge} file={som.file} keepUrl={keepUrl} />
      ) : status === 'missing_columns' ? (
        <Message kind="error">
          Rien n’a été comparé : une colonne obligatoire du fichier SOM reste à désigner. Le
          catalogue VDB continue seul.
        </Message>
      ) : status !== 'merged' ? (
        <Message kind="info">{SOM_STATUS_NOTES[status]}</Message>
      ) : null}

      {som?.preparation?.missing_weights ? (
        <Message kind="warn">
          <b>{som.preparation.missing_weights} ligne(s) SOM sans poids publié</b>
          <br />
          Elles partent au poids « manquant » de l’écran « Règles » — sauf quand VDB publie le
          poids du même produit.
          <div className={styles.models}>{som.preparation.missing_weight_models.join(' · ')}</div>
        </Message>
      ) : null}
    </>
  )
}

function SomColumnRow({
  column,
  headers,
  value,
  onChoose,
}: {
  column: SomColumn
  headers: string[]
  value: number | null
  onChoose: (role: string, index: number | null) => void
}) {
  const missing = column.required && value == null
  const source = column.source ? SOURCE_LABELS[column.source] : null
  return (
    <div className={`${styles.maprow} ${missing ? styles.missing : ''}`}>
      <div className={styles.field}>
        {column.label}
        {column.required ? <em className={styles.required}>REQUIS</em> : null}
        {source ? (
          <span className={styles.detected}>
            <Tag tone={source.tone}>{source.text}</Tag>
          </span>
        ) : null}
      </div>
      <div className={styles.arrow} aria-hidden>
        →
      </div>
      <div>
        <select
          aria-label={`Colonne SOM pour ${column.label}`}
          value={value ?? ''}
          onChange={(event) =>
            onChoose(column.role, event.target.value === '' ? null : Number(event.target.value))
          }
        >
          <option value="">— absente du fichier —</option>
          {headers.map((header, index) => (
            <option key={`${header}-${index}`} value={index}>
              {header || `colonne ${index + 1}`}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

/**
 * Le résultat de l'arbitrage, fournisseur par fournisseur.
 *
 * Ce tableau est ce qu'on relit avant d'envoyer : il dit combien de lignes
 * changent de main, et ce que le second fournisseur fait gagner.
 */
function MergeCard({
  merge,
  file,
  keepUrl,
}: {
  merge: MergeSummary
  file: SomSummary['file']
  keepUrl: (url: string) => string
}) {
  const stats: [string, string][] = [
    ['Lignes au catalogue', String(merge.rows)],
    ['Prix retenus chez VDB', String(merge.from_vdb)],
    ['Prix retenus chez SOM', String(merge.from_som)],
    ['Gagné sur les prix d’achat', `${money(Math.round(merge.savings))} €`],
  ]
  const detail: [string, number][] = [
    ['Produits présents chez les deux', merge.matched],
    ['— en stock chez VDB : prix VDB', merge.vdb_in_stock],
    ['— hors stock, SOM moins cher', merge.som_cheaper],
    ['— hors stock, VDB moins cher', merge.vdb_cheaper],
    ['Produits que SOM seul vend', merge.som_only],
    ['Produits que VDB seul vend', merge.vdb_only],
    ['Poids absents chez VDB, repris de SOM', merge.weights_from_som],
  ]

  return (
    <Card>
      <CardHead
        title="Arbitrage des prix d’achat"
        hint={
          <>
            {merge.vdb_rows} + {merge.som_rows} → {merge.rows} ligne(s)
          </>
        }
      />
      <CardBody>
        <CardNote>
          Chaque ligne du catalogue porte désormais le fournisseur chez qui son prix a été
          retenu. Un produit en stock chez VDB garde le prix VDB, même plus cher : ce qui est
          en stock part aujourd’hui. Toutes les marques sont comparées, celles écartées sans
          SOM comprises. Ce que SOM seul vend est ajouté, ce que VDB seul vend est gardé. Le
          poids, lui, ne suit pas le prix : celui que publie VDB est gardé, SOM ne comble que
          les poids absents — c’est lui qui décide de la tranche de commission Amazon et du
          transport au kilo.
        </CardNote>

        <div className={styles.prepStats}>
          {stats.map(([label, value]) => (
            <div key={label} className={styles.stat}>
              <b>{value}</b>
              <span>{label}</span>
            </div>
          ))}
        </div>

        <ul className={styles.tally}>
          {detail.map(([label, value]) => (
            <li key={label}>
              <span>{label}</span>
              <span>{value}</span>
            </li>
          ))}
        </ul>

        {merge.duplicates ? (
          <Message kind="info">
            {merge.duplicates} EAN en double dans l’un des deux fichiers : seule la ligne la
            moins chère a été retenue.
          </Message>
        ) : null}

        {file ? (
          <button
            type="button"
            className={styles.ziplink}
            onClick={() => triggerDownload(keepUrl(toBlobUrl(file)), file.name)}
          >
            Télécharger le catalogue fusionné ({file.name}, {formatBytes(file.size)})
          </button>
        ) : null}
      </CardBody>
    </Card>
  )
}

/** « vandenberg.be » plutôt que l'URL entière : le bouton dit chez qui il va. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return 'le fournisseur'
  }
}

function money(value: number): string {
  return value.toLocaleString('fr-FR')
}

/**
 * Ce que la préparation a fait de l'export brut.
 *
 * Ce n'est pas décoratif : le tri retire les trois quarts des lignes, et
 * l'écran est le seul endroit où l'on peut s'apercevoir qu'une règle a mordu
 * plus large que prévu — un poids « américain » sur un congélateur, une
 * catégorie inconnue repartie au poids par défaut.
 */
function PreparationCard({
  preparation,
  keepUrl,
}: {
  preparation: PreparationSummary
  keepUrl: (url: string) => string
}) {
  const stats: [string, number][] = [
    ['Lignes reçues', preparation.source_rows],
    ['Lignes retenues', preparation.kept],
    ['Lignes écartées', preparation.removed_total],
    ['Lignes hors stock', preparation.out_of_stock],
  ]

  return (
    <Card>
      <CardHead
        title="Préparation du catalogue fournisseur"
        hint={
          <>
            {preparation.source_rows} → {preparation.kept} ligne(s)
          </>
        }
      />
      <CardBody>
        <CardNote>
          L’export brut a été mis au format d’entrée : zéro de tête retiré des EAN, prix, poids et
          stock en tête, poids repris du fichier, colonnes inutiles supprimées.
        </CardNote>

        <div className={styles.prepStats}>
          {stats.map(([label, value]) => (
            <div key={label} className={styles.stat}>
              <b>{value}</b>
              <span>{label}</span>
            </div>
          ))}
        </div>

        {preparation.removed.length ? (
          <>
            <p className={styles.tallyHead}>Motifs d’exclusion</p>
            <ul className={styles.tally}>
              {preparation.removed.map((motif) => (
                <li key={motif.label}>
                  <span>{motif.label}</span>
                  <span>{motif.rows}</span>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {preparation.oversize.length ? (
          <Message kind="info">
            <b>
              {preparation.oversize.length} article(s) au poids « frigo américain » (220 kg)
            </b>
            <br />
            La règle ne lit que le préfixe de la référence, pas la catégorie : vérifiez qu’aucun
            petit appareil ne s’y trouve.
            <div className={styles.models}>{preparation.oversize.join(' · ')}</div>
          </Message>
        ) : null}

        {preparation.tall.length ? (
          <Message kind="info">
            <b>{preparation.tall.length} article(s) au poids « grande hauteur »</b>
            <br />
            Par la hauteur publiée ou par le préfixe de la référence — ce poids passe avant
            celui du fichier : vérifiez qu’aucun autre modèle ne s’y trouve.
            <div className={styles.models}>{preparation.tall.join(' · ')}</div>
          </Message>
        ) : null}

        {preparation.missing_weights ? (
          <Message kind="warn">
            <b>{preparation.missing_weights} article(s) sans poids publié — poids « manquant » appliqué</b>
            <br />
            Le fichier fournisseur ne donne aucun poids pour ces modèles : complétez-le, ou
            vérifiez le poids « manquant » dans l’écran « Règles ».
            <div className={styles.models}>{preparation.missing_weight_models.join(' · ')}</div>
          </Message>
        ) : null}

        {preparation.file ? (
          <button
            type="button"
            className={styles.ziplink}
            onClick={() => {
              const prepared = preparation.file
              if (prepared) triggerDownload(keepUrl(toBlobUrl(prepared)), prepared.name)
            }}
          >
            Télécharger le catalogue préparé ({preparation.file.name},{' '}
            {formatBytes(preparation.file.size)})
          </button>
        ) : null}
      </CardBody>
    </Card>
  )
}

function RulesRecap({ profile, amazonTemplate }: { profile: Profile; amazonTemplate: string | null }) {
  const { output, pricing, channels } = profile.rules
  const { preparation } = profile.rules
  const items: [string, string][] = [
    [
      'Préparation fournisseur',
      preparation.enabled
        ? `${money(preparation.min_price)} – ${money(preparation.max_price)} €`
        : 'désactivée',
    ],
    [
      "Délai d'expédition",
      `${output.handling_time_days} j en stock · ${output.handling_time_out_of_stock_days} j sinon`,
    ],
    ['Seuil colis lourd', `${String(pricing.heavy_threshold_kg).replace('.', ',')} kg`],
    ['Quantité publiée', `stock réel, ${output.max_published_quantity} au plus`],
    ['Expédition Amazon', output.amazon_shipping_group],
    ['Garantie Darty', output.darty_warranty],
    ['Fichiers', channels.map((channel) => FILE_LABELS[channel].name).join(', ')],
    ['Modèle Amazon', amazonTemplate ?? 'modèle livré'],
  ]
  return (
    <ul className={styles.recap} style={{ marginTop: 14 }}>
      {items.map(([label, value]) => (
        <li key={label}>
          <b>{label}</b> {value}
        </li>
      ))}
    </ul>
  )
}

function Results({ result, keepUrl }: { result: ConversionResponse; keepUrl: (url: string) => string }) {
  const stats: [string, number][] = [
    ['Produits convertis', result.counts.produits],
    ['Commission 15 %', result.counts.commission_15],
    ['Commission 7 %', result.counts.commission_7],
    ['Lignes ignorées', result.rejected_total],
  ]

  return (
    <div className={styles.reveal}>
      <div className={styles.stats}>
        {stats.map(([label, value]) => (
          <div key={label} className={styles.stat}>
            <b>{value}</b>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <div className={styles.downloads}>
        {result.files.map((generated) => {
          const label = generated.channel ? FILE_LABELS[generated.channel] : null
          return (
            <a
              key={generated.name}
              className={styles.download}
              style={{ borderTopColor: label?.color ?? 'var(--accent)' }}
              href={keepUrl(toBlobUrl(generated))}
              download={generated.name}
            >
              <strong>{label?.name ?? generated.name}</strong>
              <em>{label?.description ?? ''}</em>
              <em className={styles.filename}>
                {generated.name} · {formatBytes(generated.size)}
              </em>
              <span className={styles.go} style={{ color: label?.color ?? 'var(--accent)' }}>
                Télécharger
              </span>
            </a>
          )
        })}
      </div>

      {result.zip ? (
        <button
          type="button"
          className={styles.ziplink}
          onClick={() => {
            const zip = result.zip
            if (zip) triggerDownload(keepUrl(toBlobUrl(zip)), zip.name)
          }}
        >
          Télécharger tous les fichiers (.zip)
        </button>
      ) : null}

      <Card>
        <CardHead
          title="Aperçu des prix calculés"
          hint="25 premières lignes"
          actions={
            result.preview_file ? (
              <Button
                variant="secondary"
                small
                onClick={() => {
                  const sheet = result.preview_file
                  if (sheet) triggerDownload(keepUrl(toBlobUrl(sheet)), sheet.name)
                }}
              >
                Télécharger l’aperçu complet
              </Button>
            ) : undefined
          }
        />
        <CardBody>
          <Scroll>
            <Table>
              <thead>
                <tr>
                  {PREVIEW_COLUMNS.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.preview.map((row, index) => (
                  <tr key={index}>
                    {PREVIEW_COLUMNS.map((column) => {
                      const value = row[column.key]
                      if (column.key === 'commission') {
                        return (
                          <td key={column.key}>
                            <Tag tone={value === '7 %' ? 'ok' : 'warn'}>{String(value)}</Tag>
                          </td>
                        )
                      }
                      if (column.key === 'delai') {
                        // Sept jours, c'est une ligne à réapprovisionner : elle
                        // se repère d'un coup d'œil, comme la commission.
                        return (
                          <td key={column.key}>
                            <Tag tone={value === 2 ? 'ok' : 'warn'}>{String(value)} j</Tag>
                          </td>
                        )
                      }
                      if (column.key === 'fournisseur') {
                        // Le second fournisseur se repère à l'œil : sur des
                        // centaines de lignes, c'est le seul moyen de voir tout
                        // de suite ce qui a changé de main.
                        return (
                          <td key={column.key}>
                            {value ? <Tag tone={value === 'SOM' ? 'warn' : 'ok'}>{String(value)}</Tag> : ''}
                          </td>
                        )
                      }
                      return (
                        <td key={column.key} className={typeof value === 'number' ? numeric : undefined}>
                          {value ?? ''}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroll>
        </CardBody>
      </Card>

      {result.rejected.length ? (
        <Message kind="error">
          <b>{result.rejected_total} ligne(s) ignorée(s)</b>
          <br />
          {result.rejected.slice(0, 8).map((row) => (
            <span key={row.row}>
              ligne {row.row} — {row.reasons.join(', ')}
              <br />
            </span>
          ))}
          {result.rejected_total > 8 ? <>… et {result.rejected_total - 8} autre(s)</> : null}
        </Message>
      ) : (
        <Message kind="ok">Toutes les lignes du catalogue ont été converties.</Message>
      )}
    </div>
  )
}
